import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Consume the "#token=..." fragment that the Discord OAuth callback appends
// on redirect. Runs synchronously so the token is available before the first
// /auth/me call fires.
function absorbFragmentToken() {
  if (typeof window === "undefined") return null;
  const h = window.location.hash || "";
  const match = h.match(/(?:^|[&#])token=([^&]+)/);
  if (!match) return null;
  const token = decodeURIComponent(match[1]);
  localStorage.setItem("ff_token", token);
  // Strip the token from the URL bar so it isn't shareable/history-logged.
  const clean = window.location.pathname + window.location.search;
  window.history.replaceState({}, "", clean);
  return token;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    absorbFragmentToken();
    (async () => {
      try {
        // /user/me returns richer profile fields for public/user role; falls back
        // to /auth/me for admin/employee where /user/me works too.
        const { data } = await api.get("/user/me");
        setUser(data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refresh = async () => {
    try {
      const { data } = await api.get("/user/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("ff_token", data.token);
    const me = await refresh();
    return me || data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("ff_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
